import { useCallback, useEffect, useRef } from 'react';

export const OVERLAY_HISTORY_TOKEN_KEY = '__menuLabOverlayToken';

interface OverlayLayer {
  token: string;
  onDismiss: () => void;
  canDismiss: () => boolean;
}

let layers: OverlayLayer[] = [];
let pendingContinuation: (() => void) | null = null;
let fallbackCounter = 0;

function stateRecord(state: unknown): Record<string, unknown> {
  return state && typeof state === 'object' && !Array.isArray(state) ? state as Record<string, unknown> : {};
}

function tokenFromState(state: unknown): string | null {
  const value = stateRecord(state)[OVERLAY_HISTORY_TOKEN_KEY];
  return typeof value === 'string' && value ? value : null;
}

function nextToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return `overlay:${crypto.randomUUID()}`;
  fallbackCounter += 1;
  return `overlay:fallback:${fallbackCounter}`;
}

function removeLayer(token: string): OverlayLayer | null {
  const index = layers.findIndex(layer => layer.token === token);
  if (index < 0) return null;
  const [layer] = layers.splice(index, 1);
  return layer ?? null;
}

function topLayer(): OverlayLayer | null {
  return layers[layers.length - 1] ?? null;
}

function continueNavigation(): void {
  const next = pendingContinuation;
  pendingContinuation = null;
  if (next) queueMicrotask(next);
}

export function handleOverlayPopState(state: unknown): boolean {
  const top = topLayer();
  if (!top) {
    if (pendingContinuation) {
      continueNavigation();
      return true;
    }
    return false;
  }

  const targetToken = tokenFromState(state);
  if (targetToken === top.token) return true;

  if (!top.canDismiss()) {
    window.history.forward();
    return true;
  }

  removeLayer(top.token);
  top.onDismiss();
  continueNavigation();
  return true;
}

export function closeOverlayBeforeAction(continuation: () => void): boolean {
  const top = topLayer();
  if (!top) return false;
  if (!top.canDismiss()) return true;

  pendingContinuation = continuation;
  if (tokenFromState(window.history.state) === top.token) {
    window.history.back();
  } else {
    removeLayer(top.token);
    top.onDismiss();
    continueNavigation();
  }
  return true;
}

export function closeOverlayBeforeNavigation(continuation: () => void): boolean {
  return closeOverlayBeforeAction(continuation);
}

/**
 * Completes an action that already succeeded while its BlockingSheet is still marked busy.
 * The layer is removed first so the popstate handler cannot reject the unwind via canDismiss().
 * Use only after the guarded action has finished successfully.
 */
export function completeOverlayAction(continuation: () => void): boolean {
  const top = topLayer();
  if (!top) return false;

  pendingContinuation = continuation;
  const currentOwnsLayer = tokenFromState(window.history.state) === top.token;
  removeLayer(top.token);
  top.onDismiss();

  if (currentOwnsLayer) window.history.back();
  else continueNavigation();
  return true;
}

export type OverlayHistoryCleanupMode = 'immediate' | 'deferred';

export function useOverlayHistoryLayer(open: boolean, onDismiss: () => void, canDismiss: boolean | (() => boolean) = true, cleanupMode: OverlayHistoryCleanupMode = 'immediate'): () => void {
  const tokenRef = useRef<string>(nextToken());
  const dismissRef = useRef(onDismiss);
  const canDismissRef = useRef(canDismiss);
  dismissRef.current = onDismiss;
  canDismissRef.current = canDismiss;

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const token = tokenRef.current;
    layers.push({
      token,
      onDismiss: () => dismissRef.current(),
      canDismiss: () => typeof canDismissRef.current === 'function' ? canDismissRef.current() : canDismissRef.current
    });
    window.history.pushState({
      ...stateRecord(window.history.state),
      [OVERLAY_HISTORY_TOKEN_KEY]: token
    }, '', window.location.href);
    return () => {
      const wasTop = topLayer()?.token === token;
      removeLayer(token);
      if (typeof window === 'undefined' || !wasTop) return;
      const unwindHistory = () => {
        if (tokenFromState(window.history.state) === token && !pendingContinuation) {
          window.history.back();
        }
      };
      if (cleanupMode === 'deferred') window.setTimeout(unwindHistory, 0);
      else unwindHistory();
    };
  }, [open, cleanupMode]);

  return useCallback(() => {
    const allowed = typeof canDismissRef.current === 'function' ? canDismissRef.current() : canDismissRef.current;
    if (!open || typeof window === 'undefined' || !allowed) return;
    const token = tokenRef.current;
    const top = topLayer();
    if (top?.token !== token) {
      removeLayer(token);
      dismissRef.current();
      return;
    }
    if (tokenFromState(window.history.state) === token) {
      window.history.back();
      return;
    }
    removeLayer(token);
    dismissRef.current();
  }, [open]);
}
